import React, { Component, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeContextCacher } from 'common/utils/context';
import fetchActions from './actions/fetch';
import prepAndSendActions from './actions/prep-and-send';
import deepIntegrationContextActions from './actions/deep-integration';
import bulkSendActions from './actions/bulk-send';
import signatureActions from './actions/signature';
import adminActions from './actions/admin';
import editorActions from './actions/editor';
import moarActions from './actions/moar';
import adminServices from 'admin/services';
import docToTemplateActions from './actions/doc-to-template';
import sharepointActions from './actions/sharepoint';
/*
  The goal of AppActions is to abstract the way we talk to the server.
  Redux Action Creators, Components, and tests never have to know or care about
  implementation details:
  * URL
  * GET or POST
  * Do parameters go in the query or body?
  * If body, is it JSON encoded or Form parameters?

  Actions are just ordinary functions that return a promise. This also makes it
  easy to write mock actions and start writing UI code while the backend is
  still being built. That also makes them easy to mock in tests, becuase you
  only really care about the input and output. Tests shouldn't really care about
  implementation details.

*/
export type AppActions = (
  {
    admin: typeof adminServices,
  }
  & ReturnType<typeof editorActions>
  & ReturnType<typeof moarActions>
  // & ReturnType<typeof bulkSendActions>
  & ReturnType<typeof fetchActions>
  & ReturnType<typeof prepAndSendActions>
  & ReturnType<typeof deepIntegrationContextActions>
  & ReturnType<typeof docToTemplateActions>
  & ReturnType<typeof sharepointActions>
  // & ReturnType<typeof signatureActions>
)

// @ts-ignore I can't really create a default context here
export const appContext: React.Context<AppActions> = React.createContext({});

interface AppContextProp {
  appContext: AppActions
}

export function withAppContext<P>(
  Component: React.ComponentType<P & AppContextProp & React.RefAttributes<{}>>,
) {
  // Using a named function will name the forwarded ref.
  // https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props: P, ref: React.Ref<any>) => {
    return (
      <appContext.Consumer>
        {(appContext: AppActions) => (
          <Component {...props} appContext={appContext} ref={ref} />
        )}
      </appContext.Consumer>
    );
  });
}

interface User {

}

interface Props {
  csrfToken: string,
  csrfTokenAttachmentDelete: string,
  preloadedTsmGroupKey: string,
  initialUser?: User
  createMock?: <T>(key: keyof AppActions, orig: T) => T
}

interface State {
  // TODO: Remove this. It's leftover from before we started using Redux.
  user?: User
}

export default class AppContext extends Component<Props, State> {
  static propTypes = {
    csrfToken: PropTypes.string.isRequired,
    csrfTokenAttachmentDelete: PropTypes.string,
    preloadedTsmGroupKey: PropTypes.string,
    initialUser: PropTypes.shape({
      primarySignatureGuid: PropTypes.string,
      settings: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
      }).isRequired,
    }),
    children: PropTypes.node,
    createMock: PropTypes.func,

    termsURL: PropTypes.string,
    privacyURL: PropTypes.string,
  };

  state = {
    user: this.props.initialUser,
  };

  contextCache = makeContextCacher<AppActions & { data: State }>();

  actions: AppActions;

  constructor(props: Props) {
    super(props);
    this.actions = this.buildActions();
  }

  buildActions = () => {
    const actions: AppActions = {
      ...fetchActions(),
      ...prepAndSendActions(this.props.csrfToken),
      ...deepIntegrationContextActions(this.props.csrfToken),
      ...bulkSendActions(this.props.csrfToken),
      ...signatureActions(this.props.preloadedTsmGroupKey),
      ...adminActions(),
      ...moarActions(this.props.csrfToken),
      ...docToTemplateActions(),
      ...editorActions(
        this.props.csrfToken,
        this.props.csrfTokenAttachmentDelete,
        this.props.preloadedTsmGroupKey,
      ),
      ...sharepointActions(),
      admin: adminServices,
    };

    const createMock = this.props.createMock;
    if (createMock != null) {
      return Object.keys(actions).reduce((actions, key) => {
        // @ts-ignore
        actions[key] = createMock(key, actions[key]);
        return actions;
      }, actions);
    }
    return actions;
  };

  render() {
    const context = this.contextCache(Object.assign({
      data: this.state,
    }, this.actions));

    return (
      <appContext.Provider value={context}>
        {this.props.children}
      </appContext.Provider>
    );
  }
}

export function useAppContext() {
  const context = useContext(appContext);
  return context;
}
